'use client';

import { useTranslations } from 'next-intl';
import React, { useRef, useState } from 'react';

import ProgramHighlightCard from '@/app/[locale]/(default-layout)/program/_components/highlights/ProgramHighlightCard';
import { HighlightItem } from '@/app/[locale]/(default-layout)/specials/_components/SpecialsHeroContent';

import { ButtonWithIconStyle } from '@/components/buttons/ButtonWithIcon';

import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';
import { IconBack16 } from '@cineamo/ui/src/icons/ic-back';
import { IconEyeCrossed16 } from '@cineamo/ui/src/icons/ic-eyeCrossed';
import { IconEyeOpen16 } from '@cineamo/ui/src/icons/ic-eyeOpen';
import { IconForth16 } from '@cineamo/ui/src/icons/ic-forth';

import MobileOrDesktopRendererSSR from '@cineamo/composables/src/MobileOrDesktopRendererSSR';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ProgramHighlightsSectionProps = Readonly<{
    highlights: HighlightItem[];
    selectedDate?: string | 'all';
}>;

export default function ProgramHighlightsSection({
    highlights,
    selectedDate
}: ProgramHighlightsSectionProps): React.JSX.Element | null {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [mobileIndex, setMobileIndex] = useState(0);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { styleFadedState } = useScrollState({
        scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        },
        refreshTrigger: highlights.length
    });

    if (highlights.length === 0) {
        return null;
    }

    const title =
        selectedDate === 'all'
            ? t('specials.our-highlights')
            : t('program.our-highlights-on', {
                  date: `${format.dateTime.formatWeekday(selectedDate ?? '', 'short').replace('.', '')}, ${format.dateTime.formatDayMonth(selectedDate ?? '')}`
              });

    const EyeIcon = isCollapsed ? IconEyeCrossed16 : IconEyeOpen16;

    const safeMobileIndex = mobileIndex < highlights.length ? mobileIndex : 0;

    function onMobilePrev() {
        setMobileIndex((prev) => (prev - 1 + highlights.length) % highlights.length);
    }

    function onMobileNext() {
        setMobileIndex((prev) => (prev + 1) % highlights.length);
    }

    return (
        <MobileOrDesktopRendererSSR
            desktopContent={
                <div className="flex flex-col gap-8 py-16">
                    {/* Header */}
                    <div className="flex items-center gap-10">
                        <span className="text-body-bold-16 truncate">{title}</span>
                        <button
                            type="button"
                            className="shrink-0 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={() => setIsCollapsed(!isCollapsed)}>
                            <EyeIcon className="size-16" />
                        </button>
                    </div>

                    {/* Cards slider */}
                    {!isCollapsed && (
                        <div className="relative">
                            <div
                                className="flex flex-row gap-8 overflow-x-auto no-scrollbar"
                                ref={scrollContainerRef}
                                style={styleFaded(styleFadedState, 3)}>
                                {highlights.map((highlight) => (
                                    <ProgramHighlightCard
                                        key={highlight.showing.id}
                                        highlight={highlight}
                                        className="w-[614px] shrink-0"
                                    />
                                ))}
                            </div>
                            {highlights.length > 1 && (
                                <BackAndForthButtonOverlay
                                    scrollContainerRef={scrollContainerRef}
                                    buttonWithIconStyle={ButtonWithIconStyle.transparent}
                                    renderOutside
                                />
                            )}
                        </div>
                    )}
                </div>
            }
            mobileContent={
                <div className="flex flex-col gap-8 py-8">
                    {/* Header with arrows */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-8">
                            <span className="text-body-bold-16 truncate">{title}</span>
                            <button
                                type="button"
                                className="shrink-0 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                                onClick={() => setIsCollapsed(!isCollapsed)}>
                                <EyeIcon className="size-16" />
                            </button>
                        </div>

                        {!isCollapsed && highlights.length > 1 && (
                            <div className="flex items-center justify-between w-[72px]">
                                <button
                                    type="button"
                                    className="cursor-pointer"
                                    onClick={onMobilePrev}>
                                    <IconBack16 className="size-16" />
                                </button>
                                <span className="text-body-bold-12">
                                    {safeMobileIndex + 1}/{highlights.length}
                                </span>
                                <button
                                    type="button"
                                    className="cursor-pointer"
                                    onClick={onMobileNext}>
                                    <IconForth16 className="size-16" />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Single card */}
                    {!isCollapsed && (
                        <ProgramHighlightCard
                            key={highlights[safeMobileIndex].showing.id}
                            highlight={highlights[safeMobileIndex]}
                        />
                    )}
                </div>
            }
        />
    );
}
